<template>
  <b-modal
    v-model="show"
    title="Import/Export Excel"
    size="xl"
    dialog-class="modal-xxl"
    @ok="handleImport"
    @cancel="closeModal"
    :ok-disabled="!importData.length || importLoading"
    :cancel-disabled="importLoading"
  >
    <div class="px-5">
      <b-row class="mb-4 align-items-end">
        <b-col md="4">
          <h6>1. Tải template Excel</h6>
          <p class="text-muted small">Tải file template để nhập dữ liệu theo đúng định dạng</p>
          <b-button
            variant="success"
            size="sm"
            @click="$emit('download-template')"
            :disabled="loading"
          >
            <i class="fas fa-download mr-1"></i>
            Tải template
          </b-button>
        </b-col>
        <b-col md="8">
          <h6>2. Import dữ liệu từ Excel</h6>
          <p class="text-muted small">Chọn file Excel đã điền dữ liệu để import</p>
          <b-input-group>
            <b-form-file
              v-model="selectedFile"
              accept=".xlsx,.xls"
              @change="handleFileChange"
              :disabled="importLoading"
            />
            <b-input-group-append>
              <b-button
                variant="secondary"
                :disabled="!selectedFile || importLoading"
                @click="clearSelectedFile"
                v-b-tooltip.hover
                title="Xóa file"
              >
                <i class="fas fa-trash text-danger"></i>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-col>
      </b-row>

      <hr>

      <div v-if="importData.length > 0" class="mt-4 preview-table-big">
        <h6>Dữ liệu preview ({{ importData.length }} dòng, {{ totalErrors }} lỗi):</h6>
        
        <div class="mb-3">
          <b-button-group>
            <b-button 
              variant="outline-primary" 
              :pressed="filterType === 'all'"
              @click="filterType = 'all'"
              size="sm"
            >
              Tất cả ({{ importData.length }})
            </b-button>
            <b-button 
              variant="outline-danger" 
              :pressed="filterType === 'errors'"
              @click="filterType = 'errors'"
              size="sm"
            >
              Có lỗi ({{ totalErrors }})
            </b-button>
            <b-button 
              variant="outline-success" 
              :pressed="filterType === 'success'"
              @click="filterType = 'success'"
              size="sm"
            >
              Thành công ({{ totalSuccess }})
            </b-button>
          </b-button-group>
        </div>
        <div class="table-responsive" style="max-height: 500px; overflow-y: auto;">
          <b-table
            :items="filteredPaginatedData"
            :fields="previewFields"
            small
            striped
            hover
            class="preview-table-big"
            :row-class="getRowClass"
            :per-page="PER_PAGE"
            :current-page="previewCurrentPage"
            :busy="importLoading"
          >
            <template v-slot:cell(STT)="row">
              <span>{{ (previewCurrentPage - 1) * 10 + row.index + 1 }}</span>
            </template>
            <template v-slot:cell(productCode)="row">
              <span :class="{ 'text-danger': !row.item.productCode }">
                {{ row.item.productCode || 'Thiếu' }}
              </span>
            </template>
            <template v-slot:cell(productName)="row">
              <span :class="{ 'text-danger': !row.item.productName }">
                {{ row.item.productName || 'Thiếu' }}
              </span>
            </template>
            <template v-slot:cell(storeId)="row">
              <span :class="{ 'text-danger': !row.item.storeId }">
                {{ row.item.storeId || 'Thiếu' }}
              </span>
            </template>
            <template v-slot:cell(storeName)="row">
              <span :class="{ 'text-danger': !row.item.storeName }">
                {{ row.item.storeName || 'Thiếu' }}
              </span>
            </template>
            <template v-slot:cell(sellingPrice)="row">
              <span :class="{ 'text-danger': row.item.sellingPrice && isNaN(Number(row.item.sellingPrice)) }">
                {{ row.item.sellingPrice || '-' }}
              </span>
            </template>
            <template v-slot:cell(wholesalePrice)="row">
              <span :class="{ 'text-danger': row.item.wholesalePrice && isNaN(Number(row.item.wholesalePrice)) }">
                {{ row.item.wholesalePrice || '-' }}
              </span>
            </template>
            <template v-slot:cell(listedPrice)="row">
              <span :class="{ 'text-danger': row.item.listedPrice && isNaN(Number(row.item.listedPrice)) }">
                {{ row.item.listedPrice || '-' }}
              </span>
            </template>
            <template v-slot:cell(updateChildProducts)="row">
              <span :class="{ 'text-danger': row.item.updateChildProducts && !isValidBoolean(row.item.updateChildProducts) }">
                {{ formatBooleanValue(row.item.updateChildProducts) }}
              </span>
            </template>
            <template v-slot:cell(errors)="row">
              <div v-if="row.item.rowErrors && row.item.rowErrors.length > 0">
                <small class="text-danger preview-error-big">
                  <ul class="mb-0 pl-3" style="font-size: 15px;">
                    <li v-for="error in row.item.rowErrors" :key="error">
                      {{ error }}
                    </li>
                  </ul>
                </small>
              </div>
              <span v-else class="text-success">
                <small style="font-size: 15px;">✓ OK</small>
              </span>
            </template>
          </b-table>
        </div>
        
        <div v-if="filteredData.length > 10" class="mt-3">
          <b-pagination
            v-model="previewCurrentPage"
            :total-rows="filteredData.length"
            :per-page="PER_PAGE"
            size="md"
            align="center"
            first-class="page-item-first btn btn-icon btn-md m-1"
            prev-class="page-item-prev btn btn-icon btn-md m-1"
            next-class="page-item-next btn btn-icon btn-md m-1"
            last-class="page-item-last btn btn-icon btn-md m-1"
            page-class="btn btn-icon btn-md border-0 m-1"
          >
            <template v-slot:first-text>
              <span>
                <i class="ki ki-double-arrow-back icon-xs" />
              </span>
            </template>
            <template v-slot:prev-text>
              <i class="ki ki-arrow-back icon-xs" />
            </template>
            <template v-slot:next-text>
              <i class="ki ki-arrow-next icon-xs" />
            </template>
            <template v-slot:last-text>
              <span class="text-info">
                <i class="ki ki-double-arrow-next icon-xs" />
              </span>
            </template>
          </b-pagination>
        </div>
      </div>
    </div>

    <template v-slot:modal-footer>
      <b-button variant="secondary" @click="closeModal" :disabled="importLoading">
        Hủy
      </b-button>
      <b-button 
        variant="primary" 
        @click="handleImport" 
        :disabled="!importData.length || importLoading || hasImportErrors"
      >
        <b-spinner small v-if="importLoading"></b-spinner>
        {{ importLoading ? 'Đang import...' : 'Import' }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
export default {
  name: 'ProductPriceImportExportModal',
  props: {
    show: Boolean,
    loading: Boolean,
    importLoading: Boolean,
    previewFields: Array,
    importData: Array,
    hasImportErrors: Boolean,
    totalErrors: Number,
  },
  data() {
    return {
      selectedFile: null,
      previewCurrentPage: 1,
      filterType: 'all', // ['all', 'errors', 'success']
      PER_PAGE: 10,
    };
  },
  computed: {
    filteredData() {
      switch (this.filterType) {
        case 'errors':
          return this.importData.filter(item => item.isError);
        case 'success':
          return this.importData.filter(item => !item.isError);
        default:
          return this.importData;
      }
    },
    filteredPaginatedData() {
      const start = (this.previewCurrentPage - 1) * 10;
      const end = start + 10;
      return this.filteredData.slice(start, end);
    },
    totalSuccess() {
      return this.importData.filter(item => !item.isError).length;
    },
  },
  methods: {
    closeModal() {
      this.$emit('close');
      this.selectedFile = null;
      this.previewCurrentPage = 1;
      this.filterType = 'all';
    },
    handleFileChange(event) {
      const file = event.target ? event.target.files[0] : event;
      this.selectedFile = file;
      this.$emit('file-change', event);
      this.previewCurrentPage = 1;
      this.filterType = 'all';
    },
    handleImport() {
      this.$emit('import', this.selectedFile);
    },
    clearSelectedFile() {
      this.selectedFile = null;
      this.previewCurrentPage = 1;
      this.filterType = 'all';
      this.$emit('file-change', { target: { files: [] } });
      this.$emit('clear-data');
    },
    getRowClass(item) {
      return item.isError ? 'table-danger error-row' : '';
    },
    isValidBoolean(value) {
      if (value === undefined || value === '') return true;
      const lowerValue = String(value).toLowerCase();
      return ['true', 'false', '1', '0', 'có', 'không', 'yes', 'no'].includes(lowerValue);
    },
    formatBooleanValue(value) {
      if (value === undefined || value === '') return '-';
      const lowerValue = String(value).toLowerCase();
      if (['true', '1', 'có', 'yes'].includes(lowerValue)) return 'Có';
      if (['false', '0', 'không', 'no'].includes(lowerValue)) return 'Không';
      return value;
    },
  },
};
</script>

<style scoped>
.preview-table-big .table tbody tr:empty {
  display: none;
}

.preview-table-big .table tbody tr:not(:has(td)) {
  display: none;
}

/* Custom modal size */
:deep(.modal-xxl) {
  max-width: 95vw !important;
  width: 95vw !important;
}

:deep(.modal-xxl .modal-dialog) {
  max-width: 95vw !important;
  width: 95vw !important;
  margin: 1.75rem auto;
}

:deep(.modal-xxl .modal-content) {
  max-width: 95vw !important;
  width: 95vw !important;
}

:deep(.modal-xxl .modal-body) {
  max-height: 80vh;
  overflow-y: auto;
}
</style>

